import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTaskPresentations } from '../../../hooks/price/useTaskPresentations';
import { addNewPresentationToTask } from '../../../helpers/taskPresentationHelpers';
import { useAllTaskPresentationsPriceRecorded } from '../../../hooks/price/useAllTaskPresentationsPriceRecorded';
import { usePriceSessionInfo } from '../../../hooks/price/usePriceSessionInfo';
import { useFormScrollPosition } from '../../../hooks/useFormScrollPosition';
import { usePriceData } from '../../../hooks/usePriceData';
import { useForm, useWatch } from 'antd/es/form/Form';
import { trimValues } from '../../../utils/trimValues';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import RespondentDetailForm from '../../../components/price/RespondentDetail';
import { useTranslation } from 'react-i18next';
import SessionSummary from '../../../components/price/SessionSummary';
import AggregatePresentations from '../../../components/price/AggregatePresentations';
import CollectionSessionInfoForm from '../../../components/price/CollectionSessionInfoForm';
import PriceAddPresentationForm from '../../../components/price/PriceAddPresentationForm';
import MobileFloatingActionBar from '../../../components/price/MobileFloatingActionBar';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import { localDataService } from '../../../services/localDataService';
import { Carousel, Spin } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import TaskPresentationList from '../../../components/price/TaskPresentationList';
import BasicCard from '../../../components/basicCard/BasicCard';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import FormModelComponent from '../../../components/model/FormModelComponent';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import AddProductForm from '../../../components/product/AddProduct';
import { PresentationStatusEnum } from '../../../enums/PresentationStatusEnum';
import { PriceStatusEnum } from '../../../enums/PriceStatusEnum';
import { Plus, XSquare } from 'react-bootstrap-icons';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { usePresentationFormOptions } from '../../../hooks/usePresentationFormOptions';
import { locationType } from '../../../types/priceTypes';
import { getLocationCoordinates } from '../../../utils/getLocationCoordinates';
import { useMediaQuery } from 'react-responsive';
import RESOURCE_CONFIGS from '../../../constants/resourceConfig';
import { usePermissions } from '../../../hooks/usePermissions';
import { PermissionEnum } from '../../../enums/PermissionEnum';
import { RoleName } from '../../../enums/RoleNameEnm';
import { updateTask } from '../../../api/services/taskService';
import { useUpdateTaskStatusToInProgress } from '../../../hooks/task/useUpdateTaskStatusToInProgress';

interface CollectionSessionInfoType {
  inspector: any[];
  typeOfPosition: string;
  establishment: string;
  date: any;
  establishmentId?: string;
  locationId?: string;
  [key: string]: any;
}

const initialCollectionSessionInfo: CollectionSessionInfoType = {
  inspector: [],
  typeOfPosition: '',
  establishment: '',
  date: '',
  establishmentId: '',
  locationId: '',
};

const initialRespondentData = {
  respondentName: '',
  signature: '',
  finalRemarks: '',
};

export const PriceView: React.FC = () => {
  const navigate = useNavigate();

  const isMobile = useMediaQuery({
    query: `(max-width: ${RESOURCE_CONFIGS.RESPONSIVE_BREAKPOINTS.SM})`,
  });

  const locationRoute = useLocation();
  const [taskData, setTaskData] = useState<any>();

  const { t } = useTranslation(['price', 'product', 'form']);

  const [collectionSessionInfoDataForm] = useForm();
  const [addPresentationPriceForm] = useForm();
  const [respondentDataForm] = useForm();

  const [addPresentationForm] = useForm();

  const handleAddNewTaskPresentation = async (enrichedProduct: any) => {
    if (!triggeredFromTask) return;

    const { updatedProductsFromTask, updatedTask, insertIndex } = await addNewPresentationToTask({
      productsFromTask,
      enrichedProduct,
      currentCarouselIndex: currentCarouselProduct,
      taskData,
    });

    setProductsFromTask(updatedProductsFromTask);
    if (updatedTask) setTaskData(updatedTask);

    // Go to newly inserted next slide in Carousel
    setTimeout(() => {
      productCarouselRef.current?.goTo(insertIndex);
    }, 100);
  };

  const {
    isProductModalOpen,
    setIsProductModalOpen,
    handleCancel,
    closeNewProductModal,
    handleNewProductAddition,
  } = usePresentationFormOptions({
    form: addPresentationPriceForm,
    status: PresentationStatusEnum.APPROVED,
    addPresentationForm,
    onSuccess: handleAddNewTaskPresentation,
  });

  const [collectionSessionInfo, setCollectionSessionInfo] = useState<CollectionSessionInfoType>(
    initialCollectionSessionInfo
  );
  const [addedPresentations, setAddedPresentations] = useState<Record<string, unknown>[]>([]);
  const [addAggregatePresentations, setAddAggregatePresentations] = useState<
    Record<string, unknown>[]
  >([]);

  const [respondent, setRespondent] = useState(initialRespondentData);

  const [showAddPresentation, setShowAddPresentation] = useState<boolean>(false);
  const [showRespondent, setShowRespondent] = useState<boolean>(false);

  const { successNotification, errorNotification } = useNotificationContext();

  const [resetPresentationSection, setResetPresentationSection] = useState(0);
  const [finishRegistrationClicked, setFinishRegistrationClicked] = useState(0);

  const dateVal = useWatch('date', collectionSessionInfoDataForm);
  const typeOfPositionVal = useWatch('typeOfPosition', collectionSessionInfoDataForm);
  const establishmentVal = useWatch('establishment', collectionSessionInfoDataForm);
  const establishmentOtherVal = useWatch('establishmentOther', collectionSessionInfoDataForm);

  const [triggeredFromTask, setTriggeredFromTask] = useState<boolean>(false);
  const [productsFromTask, setProductsFromTask] = useState<any>([]);

  const { allPresentationsPriceRecorded: allTaskPricesRecorded } =
    useAllTaskPresentationsPriceRecorded({
      productsFromTask,
      triggeredFromTask,
    });

  const productCarouselRef = useRef<any>(null);

  const [currentCarouselProduct, setCurrentCarouselProduct] = useState(0);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [isConfirmButtonsDisable, setIsConfirmButtonsDisabled] = useState<boolean>(false);
  const respondentSectionRef = useRef<HTMLDivElement | null>(null);
  const desktopTaskPresentationListRef = useRef<HTMLDivElement | null>(null);
  const mobileTaskPresentationListRef = useRef<HTMLDivElement | null>(null);
  const priceRecordFormRef = useRef<HTMLDivElement | null>(null);
  const [locationCoordinates, setLocationCoordinates] = useState(null);

  const {
    productsFromTask: taskPresentations,
    setProductsFromTask: setTaskPresentations,
    loadTaskPresentations,
  } = useTaskPresentations();

  useEffect(() => {
    if (taskPresentations) {
      setProductsFromTask(taskPresentations);
    }
  }, [taskPresentations]);

  const { formPosition, scrollToTarget: scrollToPriceRecordForm } = useFormScrollPosition({
    targetRef: priceRecordFormRef,
  });

  const sessionInfo = usePriceSessionInfo({
    form: collectionSessionInfoDataForm,
    collectionSessionInfo,
    triggeredFromTask,
    taskData,
  });

  const { isOnline } = useIsOnlineContext();
  const { updateTaskStatusToInProgress } = useUpdateTaskStatusToInProgress();

  const { pricesLoading, createBulkPrice } = usePriceData({
    autoSync: true,
    sortBy: 'createTime',
    sortOrder: 'desc',
  });

  useEffect(() => {
    const taskData = locationRoute.state;
    if (taskData) {
      setTriggeredFromTask(true);
      loadTaskPresentations(taskData);
      updateTaskStatusToInProgress(taskData).then((updated) => {
        setTaskData(updated || taskData);
      });
    } else {
      setTaskData(undefined);
      setTriggeredFromTask(false);
    }
  }, [locationRoute.state, updateTaskStatusToInProgress, loadTaskPresentations]);

  const fetchLocationData = async () => {
    const coordinates = await getLocationCoordinates({ timeout: 15000 });
    if (coordinates) {
      setLocationCoordinates(coordinates as any);
      successNotification(t('locationAccessedSuccessfully'));
    } else {
      errorNotification(t('couldNotAccessLocation'));
    }
  };

  useEffect(() => {
    const fetchAndSetLocation = async () => {
      const taskDataState = locationRoute.state;
      if (!taskDataState) {
        const location = await getLocationCoordinates({ timeout: 15000 });
        setLocationCoordinates(location as any);
        if (location) {
          const locationData = await referenceDataSyncService.getLocationByCoordinates(
            location.lat,
            location.long
          );
          if (locationData && locationData.length > 0) {
            const { district, city } = locationData[0];
            collectionSessionInfoDataForm.setFieldsValue({
              department: district,
              municipality: city,
            });
          }
        } else {
          errorNotification(t('couldNotAccessLocation'));
        }
      }
    };

    fetchAndSetLocation();
  }, []);

  const nextProduct = () => {
    productCarouselRef.current?.next();
  };

  const deleteAggregatePresentation = (index: number) => {
    let productIdToDelete: unknown;
    setAddAggregatePresentations((prev) =>
      prev?.filter((product, i) => {
        if (i === index) {
          productIdToDelete = product?.productRefId;
        }
        return i !== index;
      })
    );
    setAddedPresentations((prev) => prev?.filter((_, i) => i !== index));
    if (triggeredFromTask) {
      const updatedProductsFromTask = productsFromTask.map((product: any) => {
        if (product.refId === productIdToDelete) {
          const { priceData, priceAdded, ...rest } = product;
          return {
            ...rest,
            priceAdded: false,
            priceData: undefined,
          };
        }
        return product;
      });
      setProductsFromTask(updatedProductsFromTask);
    }
  };

  const handleCreatePrice = async () => {
    setIsConfirmButtonsDisabled(true);
    setIsConfirmationOpen(false);
    setIsLoadingPrices(true);

    // Yield execution to allow browser to paint the full-screen overlay before async operations
    await new Promise((resolve) => setTimeout(resolve, 150));

    try {
      let locationCoordinatesVal: locationType | null;

      const locationCoordinateResult = locationCoordinates
        ? locationCoordinates
        : await getLocationCoordinates();

      if (locationCoordinateResult) {
        locationCoordinatesVal = locationCoordinateResult;
      } else {
        locationCoordinatesVal = null;
      }

      const collectionSessionInfoValues = triggeredFromTask
        ? {
            ...collectionSessionInfo,
            establishmentId:
              collectionSessionInfo?.establishmentId ||
              taskData?.establishmentRefId ||
              taskData?.establishmentId,
            locationId:
              collectionSessionInfo?.locationId || taskData?.locationRefId || taskData?.locationId,
            date: collectionSessionInfo?.date || taskData?.assignmentDate || Date.now(),
          }
        : collectionSessionInfo;

      const addPresentationValues = addedPresentations;

      let trimmedRespondent: any = null;

      if (!triggeredFromTask) {
        collectionSessionInfoDataForm.setFieldsValue(collectionSessionInfoValues);
        addPresentationPriceForm.setFieldsValue(addPresentationValues);

        const respondentFormData = respondentDataForm.getFieldsValue();
        trimmedRespondent = trimValues(respondentFormData);
        respondentDataForm.setFieldsValue(trimmedRespondent);

        await Promise.all([
          collectionSessionInfoDataForm.validateFields(),
          respondentDataForm.validateFields(),
        ]);

        setRespondent(trimmedRespondent);
      }

      const allPriceData: any = {
        ...collectionSessionInfoValues,
        productData: addPresentationValues,
      };

      if (!triggeredFromTask && trimmedRespondent) {
        Object.assign(allPriceData, trimmedRespondent);
      }

      const inspectionBody: any = {
        date: allPriceData?.date?.valueOf
          ? allPriceData.date.valueOf()
          : allPriceData?.date || Date.now(),
        locationId: allPriceData?.locationId,
        establishmentId: allPriceData?.establishmentId,
        establishmentOther: allPriceData?.establishmentOther,
        reportedTime: Date.now(),
        taskRefId: taskData?.refId,
        coordinates: locationCoordinatesVal,
      };

      if (!triggeredFromTask) {
        inspectionBody.respondent = allPriceData?.respondent;
        inspectionBody.signatureInitial = allPriceData?.signatureInitial;
        inspectionBody.finalRemarks = allPriceData?.finalRemarks;
      }

      const prices = allPriceData?.productData?.map((price: any) => ({
        ...price,
        reportedTime: Date.now(),
        coordinates: locationCoordinatesVal,
      }));

      try {
        const result = await localDataService.createInspectionWithPrices(inspectionBody, prices);

        if (result?.inspection) {
          if (isOnline) {
            successNotification(t('priceRegisterSuccessOnline'), { duration: 10 });
          } else {
            successNotification(t('priceRegisterSuccessOffline'), { duration: 10 });
          }
        }

        setCollectionSessionInfo(initialCollectionSessionInfo);
        setAddedPresentations([]);
        setAddAggregatePresentations([]);
        setRespondent(initialRespondentData);
        collectionSessionInfoDataForm.resetFields();
        addPresentationPriceForm.resetFields();
        respondentDataForm.resetFields();
        handleRespondentCancel();

        if (triggeredFromTask) {
          navigate('/inspector');
        }
      } catch (error) {
        errorNotification(t('priceSubmissionFailed'));
      } finally {
        setIsLoadingPrices(false);
        setIsConfirmButtonsDisabled(false);
      }
    } catch (validationError: any) {
      console.error('Validation error in handleCreatePrice:', validationError);
      setIsLoadingPrices(false);
      setIsConfirmButtonsDisabled(false);
    }
  };

  const handleCollectionSessionInfoNext = async () => {
    const collectionSessionInfoFormData = collectionSessionInfoDataForm.getFieldsValue();

    const trimmedValues = trimValues(collectionSessionInfoFormData);
    collectionSessionInfoDataForm.setFieldsValue(trimmedValues);
    await collectionSessionInfoDataForm.validateFields();

    const establishmentId = await referenceDataSyncService.getEstablishmentsId(
      trimmedValues?.typeOfPosition,
      trimmedValues?.establishment
    );

    const selectedLocationId = await referenceDataSyncService.getLocationId(
      trimmedValues?.department,
      trimmedValues?.municipality
    );

    setCollectionSessionInfo({
      ...trimmedValues,
      establishmentId: establishmentId[0]?.id,
      locationId: selectedLocationId,
    });
    setShowAddPresentation(true);
    setShowRespondent(false);

    // Automatically scroll down to the price addition form (Block F / A)
    setTimeout(() => {
      priceRecordFormRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  };

  const storeAddPresentationData = async () => {
    const addPresentationFormData = addPresentationPriceForm.getFieldsValue();
    const photoVal = addPresentationPriceForm.getFieldValue('photoOrInvoice');
    const productRefId = addPresentationPriceForm.getFieldValue('productRefId');

    const photoOnlyDataUlrArr =
      photoVal
        ?.map((item: any) => {
          if (!item) return null;
          if (typeof item === 'string') return item;
          return item.image || item.dataUrl || item.url || null;
        })
        ?.filter(Boolean) || null;

    const trimmedValues = trimValues(addPresentationFormData);
    addPresentationPriceForm.setFieldsValue(trimmedValues);

    const isNotFound = !!trimmedValues?.isNotFound;
    const status = isNotFound ? PriceStatusEnum.PRODUCTNOTFOUND : PriceStatusEnum.APPROVED;

    const optimisedtrimmedValues = {
      photoOrInvoice: photoOnlyDataUlrArr,
      price: isNotFound ? null : trimmedValues?.price,
      status,
      productRefId: trimmedValues?.productRefId,
      // basketId: trimmedValues?.basketId,
      productRemarks: trimmedValues?.productRemarks,
      // unit: trimmedValues?.unit,
      reportedTime: Date.now(),
    };

    const aggregateItemValue = {
      ...trimmedValues,
      status,
      price: isNotFound ? null : trimmedValues?.price,
    };

    try {
      await addPresentationPriceForm.validateFields();
      setAddedPresentations((prevProduct: any) => {
        const index = prevProduct.findIndex(
          (product: { productRefId: any }) => product.productRefId === trimmedValues?.productRefId
        );

        if (index !== -1) {
          const updatedProduct = [...prevProduct];
          updatedProduct[index] = { ...updatedProduct[index], ...optimisedtrimmedValues };
          return updatedProduct;
        } else {
          return [...prevProduct, optimisedtrimmedValues];
        }
      });

      setAddAggregatePresentations((prevProduct: any) => {
        const index = prevProduct.findIndex(
          (product: { productRefId: any }) => product.productRefId === trimmedValues?.productRefId
        );

        if (index !== -1) {
          const updatedProduct = [...prevProduct];
          updatedProduct[index] = { ...updatedProduct[index], ...aggregateItemValue };
          return updatedProduct;
        } else {
          return [...prevProduct, aggregateItemValue];
        }
      });

      if (triggeredFromTask) {
        setProductsFromTask((prevProduct: any) => {
          const index = prevProduct.findIndex(
            (product: { refId: any }) => product?.refId === trimmedValues?.productRefId
          );

          if (index !== -1) {
            const updatedProduct = [...prevProduct];
            updatedProduct[index] = {
              ...updatedProduct[index],
              priceAdded: true,
              priceData: {
                ...trimmedValues,
                status,
                photoOrInvoice: photoOnlyDataUlrArr,
              },
            };
            return updatedProduct;
          } else {
            return [...prevProduct];
          }
        });
      }

      if (triggeredFromTask) {
        // handleCarouselAfterChange();
      }
      addPresentationPriceForm.resetFields();
      setResetPresentationSection((prev) => prev + 1);
      successNotification(t('presentationAddedToAggregate'));
    } catch (error) {
      errorNotification(t('failedToAddPresentationToAggregate'));
    }
  };

  const cancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  const finishRegistration = () => {
    setFinishRegistrationClicked((prev) => prev + 1);
    if (triggeredFromTask) {
      setIsConfirmationOpen(true);
    } else {
      setShowRespondent(true);
      setShowAddPresentation(false);
      setTimeout(() => {
        respondentSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 0);
    }
  };

  const handleRespondentCancel = () => {
    // addPresentationPriceForm.resetFields();
    respondentDataForm.resetFields();
    collectionSessionInfoDataForm.resetFields();
    setShowAddPresentation(false);
    setShowRespondent(false);
    setAddAggregatePresentations([]);
    setFinishRegistrationClicked(0);
    if (triggeredFromTask) {
      navigate('/inspector');
    }
  };

  const addPresentationToList = () => {
    storeAddPresentationData();
    nextProduct();

    // Automatically scroll to TaskPresentationList (desktop or mobile ref depending on viewport width)
    setTimeout(() => {
      const activeRef =
        window.innerWidth >= 1280 ? desktopTaskPresentationListRef : mobileTaskPresentationListRef;

      activeRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  };

  const handleCarouselAfterChange = (index: number, action?: string) => {
    setCurrentCarouselProduct(index);
    if (action === 'add') {
      const addPresentationFormData = addPresentationPriceForm.getFieldsValue();
      const trimmedValues = trimValues(addPresentationFormData);
      const isNotFound = !!trimmedValues?.isNotFound;
      const status = isNotFound ? PriceStatusEnum.PRODUCTNOTFOUND : PriceStatusEnum.APPROVED;
      setProductsFromTask((prevProductsFromTask: any) => {
        const updatedProductprice = prevProductsFromTask.map(
          (product: any, productIndex: number) => {
            if (productIndex === index - 1) {
              return {
                ...product,
                priceData: {
                  ...trimmedValues,
                  status,
                  productRefId: product?.refId,
                  productName: product?.name,
                  priceAdded: true,
                },
              };
            } else {
              return product;
            }
          }
        );
        return updatedProductprice;
      });
    }
  };

  const handleDelegatedToggle = (
    productRefId: string,
    isDelegated: boolean,
    productIndex?: number
  ) => {
    let targetRefId = productRefId;

    setProductsFromTask((prevProducts: any) =>
      prevProducts?.map((product: any, idx: number) => {
        const presentationReferenceId = product?.refId || product?.productRefId || product?.id;
        const matches =
          (productIndex !== undefined && productIndex === idx) ||
          (productRefId && (presentationReferenceId === productRefId || product === productRefId));

        if (matches) {
          if (presentationReferenceId) targetRefId = presentationReferenceId;
          return {
            ...product,
            isDelegated,
            priceAdded: isDelegated ? false : product.priceAdded,
            priceData: isDelegated ? undefined : product.priceData,
          };
        }
        return product;
      })
    );

    if (isDelegated) {
      if (targetRefId) {
        setAddedPresentations((prev: any) =>
          prev?.filter((p: any) => p?.productRefId !== targetRefId)
        );
        setAddAggregatePresentations((prev: any) =>
          prev?.filter((p: any) => p?.productRefId !== targetRefId)
        );
      }

      nextProduct();
      setTimeout(() => {
        const activeRef =
          window.innerWidth >= 1280
            ? desktopTaskPresentationListRef
            : mobileTaskPresentationListRef;

        activeRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 150);
    }
  };

  if (pricesLoading) {
    return (
      <div className="accreditation-container">
        <div className="accreditation-counts">
          <div className="total-accreditation-container">
            <div className="total-accreditation total-back-bg">
              <div className="total-accreditation-text">
                <span>{t('loadingPrice')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isLoadingPrices &&
        createPortal(
          <div className="pointer-events-auto fixed inset-0 z-[999999] flex select-none flex-col items-center justify-center bg-white/60 backdrop-blur-sm">
            <Spin size="large" />
            <p className="mt-4 text-base font-semibold tracking-wide text-gray-800">
              {t('submitting')}
            </p>
          </div>,
          document.body
        )}

      <ConfirmModelComponent
        isOpen={isConfirmationOpen}
        onClickedCancel={cancelConfirmation}
        header={t('confirmPriceCreateTitle')}
        content={<p>{t('confirmPriceCreateMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleCreatePrice,
            isDisabled: isConfirmButtonsDisable,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelConfirmation,
          },
        }}
        styleClasses="z-100"
      />
      <div className="mt-4 flex w-full justify-center">
        <div className="mt-4 text-center">
          <h1 className="mb-2 text-2xl font-bold">{t('priceMainHeading')}</h1>
          <p>{t('priceSubHeading')}</p>
        </div>
      </div>

      {/* Mobile-only Top Blocks: Block C (Task Presentation List) & Block D (Added Presentations List) */}
      {triggeredFromTask && (
        <div ref={mobileTaskPresentationListRef} className="my-2 block w-full xl:hidden">
          <TaskPresentationList presentationsFromTask={productsFromTask} />
        </div>
      )}

      <div className="my-2 block w-full xl:hidden">
        <AggregatePresentations
          products={addAggregatePresentations ?? []}
          finishRegistration={finishRegistration}
          deleteAggregatePresentation={deleteAggregatePresentation}
          productCount={addAggregatePresentations?.length}
          finishRegistrationClicked={finishRegistrationClicked}
          triggeredFromTask={triggeredFromTask}
          allTaskPricesRecorded={allTaskPricesRecorded}
        />
      </div>

      <MobileFloatingActionBar
        triggeredFromTask={triggeredFromTask}
        allTaskPricesRecorded={allTaskPricesRecorded}
        addAggregatePresentationsCount={addAggregatePresentations?.length ?? 0}
        formPosition={formPosition}
        finishRegistration={finishRegistration}
        scrollToPriceRecordForm={scrollToPriceRecordForm}
      />

      <div className="my-4 flex min-h-screen w-full flex-col gap-4 xl:flex-row">
        <div
          className="ml-0 mt-4 flex-1 overflow-y-auto rounded-lg xl:ml-4"
          ref={priceRecordFormRef}
        >
          <BasicCard>
            <div className="relative p-4 md:p-6">
              <div className="flex items-center justify-center">
                <div className="m-0 w-full md:m-4">
                  <div className="mb-4 space-y-2">
                    <h2 className="text-lg font-bold text-black md:text-xl">
                      {t('priceRecordHeading')}
                    </h2>
                    <p className="md:text-md text-sm">{t('priceRecordSubHeading')}</p>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4 md:p-8">
                    <CollectionSessionInfoForm
                      form={collectionSessionInfoDataForm}
                      priceRecord={collectionSessionInfo}
                      handleNext={handleCollectionSessionInfoNext}
                      handleCancel={handleCancel}
                      isModal={false}
                      isCitizen={false}
                      finishRegistrationClicked={finishRegistrationClicked}
                      triggeredFromTask={triggeredFromTask}
                      taskData={taskData}
                    />
                  </div>

                  {showAddPresentation && !triggeredFromTask && (
                    <div className="mt-6 rounded-lg border border-gray-200 p-4 md:p-8">
                      <PriceAddPresentationForm
                        form={addPresentationPriceForm}
                        addPresentationToList={addPresentationToList}
                        resetPresentationSection={resetPresentationSection}
                        triggeredFromTask={triggeredFromTask}
                        taskData={taskData}
                      />
                    </div>
                  )}

                  {/* ------------- */}

                  {!triggeredFromTask && showRespondent && (
                    <div
                      className="mt-6 rounded-lg border border-gray-200 p-2 md:p-8"
                      ref={respondentSectionRef}
                    >
                      <RespondentDetailForm
                        form={respondentDataForm}
                        respondent={respondent}
                        handleSubmit={handleCreatePrice}
                        handleCancel={handleRespondentCancel}
                        isModal={false}
                        isCitizen={false}
                        disabled={isLoadingPrices}
                        fetchLocationData={!locationCoordinates && fetchLocationData}
                      />
                    </div>
                  )}
                </div>
              </div>
              {showAddPresentation && triggeredFromTask && (
                <div className="mt-4 md:mt-2">
                  <div className="mb-4 flex flex-col gap-2 px-0 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                    <h3 className="mb-0 text-lg font-bold text-black md:text-xl">
                      {t('price:presentationListHeading')}
                    </h3>
                  </div>
                  <Carousel
                    ref={productCarouselRef}
                    arrows
                    className="carousel-arrows h-auto w-full rounded-lg"
                    infinite={false}
                    beforeChange={(_from, to) => setCurrentCarouselProduct(to)}
                    afterChange={handleCarouselAfterChange}
                  >
                    {productsFromTask?.map((product: any, index: number) => (
                      <div key={product?.refId} className="mx-auto w-full">
                        <div className="w-full rounded-lg p-0 md:p-4">
                          <div className="rounded-lg border border-gray-200 p-4">
                            {index === currentCarouselProduct ? (
                              <PriceAddPresentationForm
                                form={addPresentationPriceForm}
                                addPresentationToList={addPresentationToList}
                                resetPresentationSection={resetPresentationSection}
                                triggeredFromTask={triggeredFromTask}
                                taskData={taskData}
                                presentationFromTask={product}
                                isActive={true}
                                onDelegatedToggle={(refId, isDel) =>
                                  handleDelegatedToggle(refId, isDel, index)
                                }
                                onNewProductCreated={handleAddNewTaskPresentation}
                              />
                            ) : (
                              <div className="flex min-h-[250px] items-center justify-center p-4 font-medium text-gray-400">
                                {product?.name}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </Carousel>
                </div>
              )}
            </div>
          </BasicCard>
        </div>

        <div className="hidden xl:block xl:w-1/4 xl:min-w-[280px]">
          <SessionSummary
            date={sessionInfo.date}
            typeOfPosition={sessionInfo.typeOfPosition}
            establishment={sessionInfo.establishment}
            establishmentOther={sessionInfo.establishmentOther}
            products={
              triggeredFromTask ? (productsFromTask ?? []) : (addAggregatePresentations ?? [])
            }
          />
          {triggeredFromTask && (
            <div ref={desktopTaskPresentationListRef}>
              <TaskPresentationList presentationsFromTask={productsFromTask} />
            </div>
          )}
          <AggregatePresentations
            products={addAggregatePresentations ?? []}
            finishRegistration={finishRegistration}
            deleteAggregatePresentation={deleteAggregatePresentation}
            productCount={addAggregatePresentations?.length}
            finishRegistrationClicked={finishRegistrationClicked}
            triggeredFromTask={triggeredFromTask}
            allTaskPricesRecorded={allTaskPricesRecorded}
          />
        </div>
      </div>

      {/* Block B: Session Summary placed at the bottom of the page on Mobile */}
      <div className="mt-4 block w-full pb-20 xl:hidden xl:pb-0">
        <SessionSummary
          date={sessionInfo.date}
          typeOfPosition={sessionInfo.typeOfPosition}
          establishment={sessionInfo.establishment}
          establishmentOther={sessionInfo.establishmentOther}
          products={
            triggeredFromTask ? (productsFromTask ?? []) : (addAggregatePresentations ?? [])
          }
        />
      </div>

      <FormModelComponent
        isOpen={isProductModalOpen}
        header={t('product:createProductTitle')}
        subheader={t('product:createProductSubTitle')}
        content={
          <AddProductForm
            form={addPresentationForm}
            handleCancel={handleCancel}
            getConfirmation={handleNewProductAddition}
            isModal={true}
            isEdit={false}
          />
        }
        onClickedCancel={closeNewProductModal}
        footerAction={{
          confimation: {
            label: t('product:createProduct'),
            onClick: handleNewProductAddition,
          },
          cancel: {
            label: t('product:cancel'),
            onClick: handleCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[60vh] md:max-h-[70vh] overflow-y-auto space-y-2"
      />
    </>
  );
};

export default PriceView;
